<script lang="ts">
  import type { Snippet } from 'svelte';
  import { WifiOff, CloudAlert } from '@lucide/svelte';
  import Sidebar from './Sidebar.svelte';
  import MobileNav from './MobileNav.svelte';
  import CommandPalette from './CommandPalette.svelte';
  import ShortcutsHelp from './ShortcutsHelp.svelte';
  import Notice from '../ui/Notice.svelte';
  import EntryEditor from '../entries/EntryEditor.svelte';
  import Onboarding from '../onboarding/Onboarding.svelte';
  import { shell } from './shell.svelte';
  import { connection } from '../connection.svelte';
  import { timer } from '../stores/timer.svelte';
  import { prefs } from '../prefs.svelte';
  import { entryEditor } from '../stores/editor.svelte';
  import { navigate } from '../router.svelte';
  import { t } from '../i18n.svelte';
  import { toast } from '../toast.svelte';
  import { errorMessage } from '../api';
  import { formatClock } from '../format';

  let { children }: { children: Snippet } = $props();

  let mobile = $state(typeof window !== 'undefined' && window.matchMedia('(max-width: 899px)').matches);
  $effect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const on = () => (mobile = mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  });

  // Document title: the running timer first, when wanted.
  $effect(() => {
    const page = shell.title ? `${shell.title} · Timepeak` : 'Timepeak';
    if (prefs.value.timerInTitle && timer.running) {
      const clock = formatClock(timer.onBreak ? timer.currentBreakMs : timer.workMs, prefs.value.showSeconds);
      document.title = `${timer.onBreak ? '⏸' : '⏱'} ${clock} · ${timer.state?.clientName || 'Timepeak'}`;
    } else {
      document.title = page;
    }
  });

  // Connection banner after a recovered outage.
  let wasDown = false;
  $effect(() => {
    if (connection.status !== 'online') wasDown = true;
    else if (wasDown) {
      wasDown = false;
      toast.success(t('conn.back'));
    }
  });

  // Keyboard shortcuts (not while typing).
  let pendingG = false;
  let gTimer: ReturnType<typeof setTimeout> | null = null;
  function onKey(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const typing = target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      shell.paletteOpen = !shell.paletteOpen;
      return;
    }
    if (typing || event.metaKey || event.ctrlKey || event.altKey || !prefs.value.keyboardShortcuts) return;
    if (document.querySelector('[aria-modal="true"]')) return;
    const key = event.key.toLowerCase();
    if (pendingG) {
      pendingG = false;
      const map: Record<string, string> = { d: '/app', t: '/app/timer', r: '/app/registraties', k: '/app/klanten', a: '/app/rapporten', i: '/app/instellingen' };
      if (map[key]) {
        event.preventDefault();
        navigate(map[key]);
      }
      return;
    }
    if (key === 'g') {
      pendingG = true;
      if (gTimer) clearTimeout(gTimer);
      gTimer = setTimeout(() => (pendingG = false), 1200);
    } else if (key === 'n') {
      event.preventDefault();
      entryEditor.open();
    } else if (key === 's') {
      event.preventDefault();
      if (timer.running) timer.stop().catch((e) => toast.error(errorMessage(e)));
      else navigate('/app/timer');
    } else if (event.key === '?') {
      shell.shortcutsOpen = true;
    }
  }
</script>

<svelte:window onkeydown={onKey} />

<div class="shell" class:mobile>
  {#if !mobile}<Sidebar />{/if}
  <div class="main">
    {#if mobile}<MobileNav />{/if}
    {#if connection.status !== 'online' || timer.queued > 0}
      {@const offline = connection.status === 'offline'}
      <Notice variant="bar" tone={offline ? 'neutral' : 'warning'} icon={offline ? WifiOff : CloudAlert} role="status">
        {#if offline}{t('conn.offline')}{:else if connection.status === 'degraded'}{t('conn.degraded')}{:else}{t('conn.queued', { count: timer.queued })}{/if}
      </Notice>
    {/if}
    <main id="main">{@render children()}</main>
  </div>
</div>

<CommandPalette />
<ShortcutsHelp />
<EntryEditor />
<Onboarding />

<style>
  .shell { display: flex; min-height: 100vh; min-height: 100dvh; }
  .main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
  main { flex: 1; min-width: 0; }
</style>
